/* ============================================================================
   sketchAnimator.js

   A browser port of the draw-on effect from X_MakeManyUSMLEOriginal4.py.
   Given a sketch PNG it traces the artwork's outlines, then animates a pen
   laying those outlines down while the finished colour fades in behind them.

   The constants and easing curves below are taken from the render engine so
   the web version matches the videos. Where a value could not be scaled
   proportionally (a 2px line at 1080px wide becomes invisible at 200px) the
   engine's value is noted next to the web one.

   Everything is plain DOM + canvas. No dependencies.
   ============================================================================ */

/* --- from the engine, unchanged ------------------------------------------ */
const INK_THRESHOLD = 128;    // euclidean distance from white, per load_sketch
const EARLY_LEVEL   = 0.10;   // DRAW_EARLY_FADE_LEVEL
const ZOOM_START    = 0.85;   // SKETCH_ZOOM_START
const ZOOM_END      = 1.00;   // SKETCH_ZOOM_END

/* The engine uses absolute seconds (3s ramp / 3s ramp of a ~9.4s draw).
   Expressed as ratios so a shorter web draw keeps the same feel. */
const EARLY_RATIO = 0.32;     // DRAW_EARLY_FADE_SEC as a share of the draw
const FINAL_RATIO = 0.32;     // DRAW_COLOR_FADE_SEC as a share of the draw

/* --- scaled for small on-screen sizes ----------------------------------- */
const EDGE_THRESHOLD = 55;    // Sobel magnitude, standing in for Canny(50,150)
const RDP_EPSILON    = 0.9;   // engine: CONTOUR_EPSILON 1.2 at 1080px
const MIN_PATH_LEN   = 9;     // engine: MIN_CONTOUR_PERIM 40 at 1080px
const PEN_COLOR      = "#464646";  // engine: PEN_STROKE_COLOR (70,70,70) BGR
const DOT_COLOR      = "#323232";  // engine: PEN_DOT_COLOR (50,50,50)

/* Every curve in the engine is this one cosine ease. */
const ease = (p) => 0.5 - 0.5 * Math.cos(Math.PI * Math.min(Math.max(p, 0), 1));

/* Colour underlay: ramp to 10%, hold flat, then ramp to 100%. */
function colourAlpha(t) {
  const fadeStart = 1 - FINAL_RATIO;
  if (t > fadeStart) {
    return EARLY_LEVEL + (1 - EARLY_LEVEL) * ease((t - fadeStart) / FINAL_RATIO);
  }
  if (t <= EARLY_RATIO) return EARLY_LEVEL * ease(t / EARLY_RATIO);
  return EARLY_LEVEL;
}

/* Pen strokes dissolve over the same final window the colour completes in,
   so the last drawn frame equals the clean PNG. */
function strokeAlpha(t) {
  const fadeStart = 1 - FINAL_RATIO;
  if (t <= fadeStart) return 1;
  return 1 - ease((t - fadeStart) / FINAL_RATIO);
}

/* ============================================================================
   1. Load the image and find the artwork inside it

   The source PNGs are square with a lot of empty margin. load_sketch() in the
   engine crops to the ink bounding box before doing anything else; we do the
   same, which is also why the art fills its tile without any CSS scale hacks.
   ============================================================================ */

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });
}

function inkBounds(img) {
  // Small probe pass — 160px is plenty to locate the bounding box.
  const probe = 160;
  const s = Math.min(probe / img.naturalWidth, probe / img.naturalHeight, 1);
  const w = Math.max(1, Math.round(img.naturalWidth * s));
  const h = Math.max(1, Math.round(img.naturalHeight * s));

  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#fff";           // flatten alpha onto white, as the engine does
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  const d = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const dr = 255 - d[i], dg = 255 - d[i + 1], db = 255 - d[i + 2];
      if (Math.sqrt(dr * dr + dg * dg + db * db) > INK_THRESHOLD) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }

  if (x1 < 0) {
    return { sx: 0, sy: 0, sw: img.naturalWidth, sh: img.naturalHeight };
  }

  // Back to natural coordinates, with a couple of pixels of breathing room.
  const pad = 2 / s;
  const sx = Math.max(0, x0 / s - pad);
  const sy = Math.max(0, y0 / s - pad);
  const sw = Math.min(img.naturalWidth - sx, (x1 - x0 + 1) / s + pad * 2);
  const sh = Math.min(img.naturalHeight - sy, (y1 - y0 + 1) / s + pad * 2);
  return { sx, sy, sw, sh };
}

/* ============================================================================
   2. Build the edge map

   The engine runs Canny, ANDs it with the ink mask, then ORs in the mask's
   morphological gradient. Canny is a lot of code for the payoff at this size,
   so a thresholded Sobel stands in for it — same job (find the internal
   outlines between colour regions), slightly thicker lines, which the pen
   simply retraces. The silhouette boundary is exact.
   ============================================================================ */

function buildEdgeMap(img, crop, size) {
  const ar = crop.sw / crop.sh;
  const w = Math.max(24, Math.round(ar >= 1 ? size : size * ar));
  const h = Math.max(24, Math.round(ar >= 1 ? size / ar : size));

  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, w, h);

  const d = ctx.getImageData(0, 0, w, h).data;
  const n = w * h;
  const gray = new Float32Array(n);
  const ink = new Uint8Array(n);

  for (let i = 0; i < n; i++) {
    const j = i * 4;
    const r = d[j], g = d[j + 1], b = d[j + 2];
    gray[i] = 0.299 * r + 0.587 * g + 0.114 * b;
    const dr = 255 - r, dg = 255 - g, db = 255 - b;
    if (Math.sqrt(dr * dr + dg * dg + db * db) > INK_THRESHOLD) ink[i] = 1;
  }

  const edge = new Uint8Array(n);

  // Internal edges: Sobel magnitude, kept only where there is ink.
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (!ink[i]) continue;
      const tl = gray[i - w - 1], tc = gray[i - w], tr = gray[i - w + 1];
      const ml = gray[i - 1],                      mr = gray[i + 1];
      const bl = gray[i + w - 1], bc = gray[i + w], br = gray[i + w + 1];
      const gx = (tr + 2 * mr + br) - (tl + 2 * ml + bl);
      const gy = (bl + 2 * bc + br) - (tl + 2 * tc + tr);
      if (Math.sqrt(gx * gx + gy * gy) > EDGE_THRESHOLD) edge[i] = 1;
    }
  }

  // Silhouette: any ink pixel touching non-ink. (MORPH_GRADIENT in the engine.)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!ink[i]) continue;
      let boundary = false;
      for (let dy = -1; dy <= 1 && !boundary; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h || !ink[ny * w + nx]) {
            boundary = true;
            break;
          }
        }
      }
      if (boundary) edge[i] = 1;
    }
  }

  return { edge, w, h };
}

/* ============================================================================
   3. Walk the edge map into ordered pen strokes

   findContours() follows closed boundaries; here we walk each connected run of
   edge pixels in the straightest available direction, which produces open
   strokes that look more like a pen than a boundary loop does. Strokes are then
   simplified and sorted longest-first, matching the engine's draw order.
   ============================================================================ */

const NEIGHBOURS = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
];

function walkStrokes({ edge, w, h }) {
  const seen = new Uint8Array(w * h);
  const strokes = [];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const start = y * w + x;
      if (!edge[start] || seen[start]) continue;

      seen[start] = 1;
      const pts = [[x, y]];
      let cx = x, cy = y, ldx = 0, ldy = 0;

      for (;;) {
        let best = -1, bestScore = -Infinity;
        for (let k = 0; k < 8; k++) {
          const [dx, dy] = NEIGHBOURS[k];
          const nx = cx + dx, ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const ni = ny * w + nx;
          if (!edge[ni] || seen[ni]) continue;
          // Prefer continuing in the current direction over turning.
          const align = ldx === 0 && ldy === 0 ? 0 : dx * ldx + dy * ldy;
          const score = align * 2 - (Math.abs(dx) + Math.abs(dy));
          if (score > bestScore) {
            bestScore = score;
            best = ni;
          }
        }
        if (best < 0) break;
        const nx = best % w, ny = (best - nx) / w;
        seen[best] = 1;
        ldx = nx - cx;
        ldy = ny - cy;
        cx = nx;
        cy = ny;
        pts.push([cx, cy]);
      }

      if (pts.length >= 2) strokes.push(pts);
    }
  }

  return strokes;
}

/* Ramer–Douglas–Peucker, iterative so long strokes can't blow the stack. */
function simplify(pts, epsilon) {
  if (pts.length < 3) return pts;

  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack = [[0, pts.length - 1]];

  while (stack.length) {
    const [first, last] = stack.pop();
    if (last <= first + 1) continue;

    const [ax, ay] = pts[first];
    const [bx, by] = pts[last];
    const dx = bx - ax, dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;

    let worst = -1, worstDist = 0;
    for (let i = first + 1; i < last; i++) {
      const [px, py] = pts[i];
      const dist = Math.abs((px - ax) * dy - (py - ay) * dx) / len;
      if (dist > worstDist) {
        worstDist = dist;
        worst = i;
      }
    }

    if (worstDist > epsilon && worst > 0) {
      keep[worst] = 1;
      stack.push([first, worst], [worst, last]);
    }
  }

  const out = [];
  for (let i = 0; i < pts.length; i++) if (keep[i]) out.push(pts[i]);
  return out;
}

function pathLength(pts) {
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  return total;
}

function buildStrokes(img, crop, traceSize, outW, outH) {
  const map = buildEdgeMap(img, crop, traceSize);
  const sx = outW / map.w;
  const sy = outH / map.h;

  return walkStrokes(map)
    .map((pts) => simplify(pts, RDP_EPSILON))
    .filter((pts) => pts.length >= 2 && pathLength(pts) >= MIN_PATH_LEN)
    .map((pts) => ({
      pts: pts.map(([x, y]) => [x * sx, y * sy]),
      len: pathLength(pts),
    }))
    .sort((a, b) => b.len - a.len)      // longest first, as the engine does
    .map((s) => s.pts);
}

/* ============================================================================
   4. Play it back
   ============================================================================ */

export function createSketchAnimation(canvas, options) {
  const {
    src,
    size,
    duration = 1900,
    penWidth = 1.3,      // engine: DRAW_LINE_THICKNESS 2 at 1080px
    dotRadius = 4.4,     // engine: PEN_DOT_RADIUS 14 at 1080px
    traceSize = 210,
    onReady,
  } = options;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let raf = 0;
  let cancelled = false;
  let state = null;

  canvas.width = Math.round(size * dpr);
  canvas.height = Math.round(size * dpr);
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  const strokeCanvas = document.createElement("canvas");
  strokeCanvas.width = canvas.width;
  strokeCanvas.height = canvas.height;
  const strokeCtx = strokeCanvas.getContext("2d");
  strokeCtx.scale(dpr, dpr);
  strokeCtx.strokeStyle = PEN_COLOR;
  strokeCtx.lineWidth = penWidth;
  strokeCtx.lineCap = "round";
  strokeCtx.lineJoin = "round";

  /* The art is letterboxed into the square canvas, keeping its aspect. */
  function drawArt(alpha) {
    const { img, crop, box } = state;
    ctx.globalAlpha = alpha;
    ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh,
                  box.x, box.y, box.w, box.h);
    ctx.globalAlpha = 1;
  }

  function drawFinal() {
    ctx.clearRect(0, 0, size, size);
    drawArt(1);
  }

  function frame(now) {
    if (cancelled) return;
    const t = Math.min(1, (now - state.startedAt) / duration);

    // Lay down every segment the easing says should exist by now.
    const eased = ease(t);
    const target = Math.floor(state.totalSegs * eased);
    if (target > state.drawn) {
      strokeCtx.beginPath();
      while (state.drawn < target && state.si < state.strokes.length) {
        const pts = state.strokes[state.si];
        if (state.pi < pts.length - 1) {
          const [x0, y0] = pts[state.pi];
          const [x1, y1] = pts[state.pi + 1];
          strokeCtx.moveTo(x0, y0);
          strokeCtx.lineTo(x1, y1);
          state.last = [x1, y1];
          state.pi++;
          state.drawn++;
        } else {
          state.si++;
          state.pi = 0;
        }
      }
      strokeCtx.stroke();
    }

    ctx.clearRect(0, 0, size, size);
    ctx.save();
    const zoom = ZOOM_START + (ZOOM_END - ZOOM_START) * eased;
    const c = size / 2;
    ctx.translate(c, c);
    ctx.scale(zoom, zoom);
    ctx.translate(-c, -c);

    drawArt(colourAlpha(t));

    ctx.globalAlpha = strokeAlpha(t);
    ctx.drawImage(strokeCanvas, 0, 0, size, size);
    ctx.globalAlpha = 1;

    if (state.last && state.drawn < state.totalSegs) {
      const [x, y] = state.last;
      ctx.beginPath();
      ctx.arc(x, y, dotRadius + 1.4, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = DOT_COLOR;
      ctx.fill();
    }

    ctx.restore();

    if (t < 1) {
      raf = requestAnimationFrame(frame);
    } else {
      drawFinal();
    }
  }

  async function prepare() {
    const img = await loadImage(src);
    if (cancelled) return;

    const crop = inkBounds(img);
    const ar = crop.sw / crop.sh;
    const box = ar >= 1
      ? { w: size, h: size / ar, x: 0, y: (size - size / ar) / 2 }
      : { w: size * ar, h: size, x: (size - size * ar) / 2, y: 0 };

    const strokes = buildStrokes(img, crop, traceSize, size, size)
      // Stroke coordinates come back in canvas space; shift into the art box.
      .map((pts) => pts.map(([x, y]) => [
        box.x + (x / size) * box.w,
        box.y + (y / size) * box.h,
      ]));

    let totalSegs = 0;
    for (const pts of strokes) totalSegs += pts.length - 1;

    state = {
      img, crop, box, strokes, totalSegs,
      drawn: 0, si: 0, pi: 0, last: null, startedAt: 0,
    };

    if (onReady) onReady();
  }

  return {
    /* Traces, then draws. Resolves once the pen starts moving. */
    async start() {
      try {
        await prepare();
      } catch {
        return;                     // a missing PNG just leaves the tile empty
      }
      if (cancelled || !state) return;

      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced || state.totalSegs === 0) {
        drawFinal();
        return;
      }

      state.startedAt = performance.now();
      raf = requestAnimationFrame(frame);
    },

    /* Skip straight to the finished artwork. */
    finish() {
      cancelAnimationFrame(raf);
      if (state) drawFinal();
    },

    destroy() {
      cancelled = true;
      cancelAnimationFrame(raf);
    },
  };
}
